import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { DutiesDisclaimer } from '@components/DutiesDisclaimer/DutiesDisclaimer'
import { useExchangeRates } from '@components/ExchangeRatesProvider'
import { IsGiftOption } from '@components/IsGiftOption/IsGiftOption'
import { ProductsSlider } from '@components/ProductsSlider/ProductsSlider'
import type { CheckedState } from '@radix-ui/react-checkbox'
import { dataTestIds } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { useCartSuggestedProducts } from '@velasca/hooks/use-cart-suggested-products'
import { useHeader } from '@velasca/hooks/use-header'
import { LockIcon } from '@velasca/icons/lock'
import { TagIcon } from '@velasca/icons/tag'
import { TruckIcon } from '@velasca/icons/truck'
import { Button } from '@velasca/ui/button'
import { CartTitle } from '@velasca/ui/cart-title'
import { SansText } from '@velasca/ui/sans-text'
import { Separator } from '@velasca/ui/separator'
import { $settings } from '@velasca/utils/globals'
import { trackEvent } from '@velasca/utils/track-event'
import { CartPageContainer } from '../CartPage.styles'
import { DesktopInfoBanner, MobileInfoBanner } from '../InfoBanner/InfoBanner'
import {
  BottomPageSeparator,
  CheckoutForm,
  DesktopStickyWrapper,
  DesktopSuggestedProducts,
  DesktopSummaryCheckoutButtonContainer,
  DesktopSummaryTitle,
  LineItemsContainer,
  MainSection,
  MediaSeparator,
  MobileSeparator,
  MobileSuggestedProducts,
  MobileSummaryCheckoutButtonContainer,
  MobileSummaryTitle,
  StickyCheckout,
  StyledKlarnaPlacement,
  StyledLineItem,
  SummaryIcon,
  SummaryItem,
  SummaryItems,
  SummaryList,
  SummarySection,
} from './CartWithItems.styles'

export const CartWithItems = () => {
  const { t } = useTranslation('global')
  const { convert } = useExchangeRates()
  const { headerHeight } = useHeader()
  const [isGiftOrder, setIsGiftOrder] = useState<CheckedState>(false)
  const { ref: bottomBarRef, inView: isStickyButtonStuck } = useInView({
    threshold: 0,
    rootMargin: '0px 0px -115px 0px',
  })

  const {
    data: cartData,
    loading,
    cartItemCount,
    suggestedProductsHandle,
  } = useCart()

  const formattedTotalPrice = useMemo(() => {
    if (!cartData) {
      return ''
    }

    return convert(cartData.total_price)
  }, [cartData, convert])

  const {
    data: suggestedProductsData,
    loading: suggestedProductsLoading,
    title,
    subtitle,
    onAddToCartAddeventName,
    onQuickAddEventName,
  } = useCartSuggestedProducts()

  if (!cartData) {
    return null
  }

  const { items, total_price } = cartData

  const handleGoToCheckoutButtonClick = () => {
    trackEvent({
      teads: {
        eventName: 'InitiateCheckout',
        data: {
          name: 'Cart',
          price: total_price,
          currency: $settings.currency,
        },
      },
    })
  }

  return (
    <>
      <DesktopInfoBanner>
        <SansText as="span" size="md" color="white">
          {t('cart_item_count', { count: cartItemCount })}:
        </SansText>
        <SansText
          size="md"
          color="white"
          fontWeight="600"
          dangerouslySetInnerHTML={{ __html: formattedTotalPrice }}
        />
      </DesktopInfoBanner>

      <CartPageContainer isLoading={loading}>
        <MainSection>
          <MobileSummaryTitle>
            <CartTitle title={t('cart_drawer_title')} />
            <MobileSeparator />
          </MobileSummaryTitle>
          <LineItemsContainer>
            {items?.map(
              (
                {
                  id,
                  handle,
                  image,
                  product_title,
                  price,
                  compareAtPrice,
                  product_type,
                  quantity,
                  options,
                },
                index,
              ) => {
                return (
                  <Fragment key={id}>
                    <StyledLineItem
                      isCartPage
                      productID={id}
                      handle={handle}
                      image={image}
                      title={product_title || ''}
                      productType={product_type}
                      initialItemQuantity={quantity}
                      color={options?.color}
                      size={options?.size}
                      material={options?.material}
                      rawCompareAtPrice={(compareAtPrice?.max ?? 0) * 100}
                      rawPrice={price}
                      imgAlt={t('cart_drawer_product_alt_template', {
                        color: options?.color,
                        material: options?.material,
                        productTitle: product_title || '',
                        productType: product_type,
                      })}
                    />
                    {index !== items.length - 1 && <Separator />}
                  </Fragment>
                )
              },
            )}
            <MobileSeparator />
          </LineItemsContainer>

          <DesktopSuggestedProducts>
            <ProductsSlider
              showProductType
              products={suggestedProductsData}
              loading={suggestedProductsLoading}
              title={title}
              subTitle={subtitle}
              breakpoints={{
                768: {
                  slidesPerView: 2.1,
                  spaceBetween: 10,
                },
                992: {
                  slidesPerView: 2.1,
                  spaceBetween: 10,
                },
              }}
              onQuickAdd={() => {
                onAddToCartAddeventName &&
                  trackEvent({
                    googleAnalytics: {
                      eventName: onQuickAddEventName,
                      data: { handle: suggestedProductsHandle },
                    },
                  })
              }}
              onAddToCart={(handle) => {
                onAddToCartAddeventName &&
                  trackEvent({
                    googleAnalytics: {
                      eventName: onAddToCartAddeventName,
                      data: { handle },
                    },
                  })
              }}
            />
          </DesktopSuggestedProducts>
        </MainSection>
        <SummarySection>
          <DesktopStickyWrapper topOffset={headerHeight}>
            <DesktopSummaryTitle>
              <CartTitle title={t('cart_drawer_title')} />
              <Separator />
            </DesktopSummaryTitle>
            <SummaryList>
              <SummaryItem>
                <SansText size="md" style={{ fontSize: '1.4rem' }}>
                  {t('cart_label_subtotal')}
                </SansText>
                <SansText
                  size="lg"
                  fontWeight="500"
                  dangerouslySetInnerHTML={{ __html: formattedTotalPrice }}
                />
              </SummaryItem>

              <SummaryItem
                css={`
                  text-align: end;
                `}
              >
                <SansText size="md" style={{ fontSize: '1.4rem' }}>
                  {t('cart_label_shipping')}
                </SansText>
                <SansText
                  fontStyle="italic"
                  size="md"
                  color="primary"
                  style={{ fontSize: '1.4rem' }}
                >
                  {t('cart_next_step_calculate')}
                </SansText>
              </SummaryItem>
              <Separator />

              <SummaryItem>
                <SansText uppercase fontWeight="500" size="md">
                  {t('cart_total_label')}
                </SansText>
                <SansText
                  size="lg"
                  fontWeight="500"
                  dangerouslySetInnerHTML={{ __html: formattedTotalPrice }}
                />
              </SummaryItem>

              <Separator />

              <SummaryItem>
                <IsGiftOption setIsGiftOrder={setIsGiftOrder} />
              </SummaryItem>

              {$settings.show_duties_text && <DutiesDisclaimer />}
              <SummaryItem>
                <CheckoutForm noValidate action="/cart" method="POST">
                  <input
                    type="hidden"
                    value={isGiftOrder ? 'T' : 'F'}
                    name="attributes[is_gift_order]"
                  />
                  <DesktopSummaryCheckoutButtonContainer>
                    <Button
                      fullWidth
                      name="checkout"
                      type="submit"
                      text={t('checkout')}
                      onClick={handleGoToCheckoutButtonClick}
                    />
                  </DesktopSummaryCheckoutButtonContainer>
                </CheckoutForm>
              </SummaryItem>

              <MediaSeparator />

              <SummaryItem>
                <StyledKlarnaPlacement amount={total_price} />
              </SummaryItem>

              <SummaryItems>
                <SummaryItem
                  css={`
                    justify-content: flex-start;
                  `}
                >
                  <SummaryIcon>
                    <TruckIcon />
                  </SummaryIcon>
                  <SansText
                    size="md"
                    style={{ fontSize: '1.4rem' }}
                    dangerouslySetInnerHTML={{
                      __html: t('cart_free_shipping'),
                    }}
                  />
                </SummaryItem>
                <SummaryItem
                  css={`
                    justify-content: flex-start;
                  `}
                >
                  <SummaryIcon>
                    <TagIcon />
                  </SummaryIcon>
                  <SansText
                    size="md"
                    style={{ fontSize: '1.4rem', fontStyle: 'italic' }}
                    dangerouslySetInnerHTML={{ __html: t('cart_coupon') }}
                  />
                </SummaryItem>
                <SummaryItem
                  css={`
                    justify-content: flex-start;
                  `}
                >
                  <SummaryIcon>
                    <LockIcon />
                  </SummaryIcon>
                  <SansText
                    size="md"
                    style={{ fontSize: '1.4rem' }}
                    dangerouslySetInnerHTML={{
                      __html: t('cart_secure_payment'),
                    }}
                  />
                </SummaryItem>
              </SummaryItems>
              <Separator />
              <SummaryItem
                css={`
                  justify-content: center;
                `}
              >
                <SansText
                  underline
                  center
                  uppercase
                  color="primary"
                  as="a"
                  href="/"
                  fontWeight="500"
                >
                  {t('cart_continue_shopping')}
                </SansText>
              </SummaryItem>
            </SummaryList>
          </DesktopStickyWrapper>
        </SummarySection>
      </CartPageContainer>

      {
        <MobileSuggestedProducts
          data-testid={dataTestIds.MOBILE_SUGGESTED_PRODUCTS}
        >
          <ProductsSlider
            showProductType
            products={suggestedProductsData}
            loading={suggestedProductsLoading}
            title={title}
            subTitle={subtitle}
            onQuickAdd={() => {
              onAddToCartAddeventName &&
                trackEvent({
                  googleAnalytics: {
                    eventName: onQuickAddEventName,
                    data: { handle: suggestedProductsHandle },
                  },
                })
            }}
            onAddToCart={(handle) => {
              onAddToCartAddeventName &&
                trackEvent({
                  googleAnalytics: {
                    eventName: onAddToCartAddeventName,
                    data: { handle },
                  },
                })
            }}
          />
        </MobileSuggestedProducts>
      }

      <BottomPageSeparator ref={bottomBarRef}>
        <Separator />
      </BottomPageSeparator>

      <StickyCheckout
        noValidate
        action="/cart"
        method="POST"
        isStuck={isStickyButtonStuck}
      >
        <input
          type="hidden"
          value={isGiftOrder ? 'T' : 'F'}
          name="attributes[is_gift_order]"
        />
        <MobileSummaryCheckoutButtonContainer>
          <Button
            fullWidth
            name="checkout"
            type="submit"
            text={t('checkout')}
          />
        </MobileSummaryCheckoutButtonContainer>

        <SansText
          underline
          center
          uppercase
          margin="0.6rem 0 0"
          color="primary"
          as="a"
          href="/"
          fontWeight="500"
        >
          {t('cart_continue_shopping')}
        </SansText>

        <SansText size="sm" fontStyle="italic">
          {t('cart_drawer_footer_message')}
        </SansText>
      </StickyCheckout>

      <MobileInfoBanner>
        <SansText as="span" size="lg" color="white">
          {t('cart_item_count', { count: cartItemCount })}:
        </SansText>
        <SansText
          size="lg"
          color="white"
          dangerouslySetInnerHTML={{ __html: formattedTotalPrice }}
        />
      </MobileInfoBanner>
    </>
  )
}
